/**
 * #1106 — stage inline image bytes offline until reconnect upload.
 *
 * Durable (survives app/tab restart) via the same `storage` service the rest
 * of the offline system uses. Bytes stay in this queue — they are never
 * merged into the request payload.
 *
 * Web create-flow note: large images exceed `localStorage` quota when base64-
 * encoded. On web we keep a session memory fallback so unsaved requests can
 * still stage inline images until the first save uploads them.
 */
import { Platform } from 'react-native';

import { storage } from './storage';

export type OfflineInlineImageRecord = {
  clientId: string;
  requestId: number | null;
  requestSection: string;
  fieldLabel?: string;
  /** Form transport key (e.g. oppProjectDescription) for create-flow flush merge. */
  transportFieldKey?: string;
  mimeType: string;
  fileName: string;
  dataBase64: string;
  createdAt: number;
};

const STORAGE_KEY = 'offline-inline-image-queue-v1';

/** Session-only fallback when web localStorage rejects large base64 payloads. */
const webMemoryQueue = new Map<string, OfflineInlineImageRecord>();

let queueLock: Promise<void> = Promise.resolve();

function withQueueLock<T>(fn: () => Promise<T>): Promise<T> {
  const run = queueLock.then(fn, fn);
  queueLock = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

function isOfflineInlineImageRecord(
  value: unknown,
): value is OfflineInlineImageRecord {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    typeof record.clientId === 'string' &&
    record.clientId.length > 0 &&
    typeof record.requestSection === 'string' &&
    typeof record.mimeType === 'string' &&
    typeof record.fileName === 'string' &&
    typeof record.dataBase64 === 'string' &&
    record.dataBase64.length > 0
  );
}

async function readQueue(): Promise<OfflineInlineImageRecord[]> {
  const parsed = await storage.getJSON<unknown>(STORAGE_KEY);
  const fromStorage = Array.isArray(parsed)
    ? parsed.filter(isOfflineInlineImageRecord)
    : [];
  if (Platform.OS !== 'web' || webMemoryQueue.size === 0) {
    return fromStorage;
  }
  const merged = new Map<string, OfflineInlineImageRecord>();
  for (const record of fromStorage) {
    merged.set(record.clientId, record);
  }
  for (const record of webMemoryQueue.values()) {
    merged.set(record.clientId, record);
  }
  return [...merged.values()];
}

async function writeQueue(records: OfflineInlineImageRecord[]): Promise<void> {
  if (records.length === 0) {
    webMemoryQueue.clear();
    await storage.removeItem(STORAGE_KEY);
    return;
  }
  try {
    await storage.setJSON(STORAGE_KEY, records);
    for (const record of records) {
      webMemoryQueue.delete(record.clientId);
    }
  } catch (error) {
    if (Platform.OS !== 'web') {
      throw error;
    }
    for (const record of records) {
      webMemoryQueue.set(record.clientId, record);
    }
  }
}

export async function stageOfflineInlineImage(
  record: OfflineInlineImageRecord,
): Promise<void> {
  await withQueueLock(async () => {
    const queue = await readQueue();
    const next = queue.filter(item => item.clientId !== record.clientId);
    next.push(record);
    await writeQueue(next);
  });
}

export async function listOfflineInlineImages(): Promise<
  OfflineInlineImageRecord[]
> {
  return withQueueLock(() => readQueue());
}

export async function removeOfflineInlineImage(
  clientId: string,
): Promise<void> {
  await withQueueLock(async () => {
    webMemoryQueue.delete(clientId);
    const queue = await readQueue();
    await writeQueue(queue.filter(item => item.clientId !== clientId));
  });
}

export function createOfflineInlineImageClientId(): string {
  return `offline-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
